
import Clarity from "@microsoft/clarity";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { CLARITY_PROJECT_ID } from "../../../project_config";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import { isSiteAdmin } from "../../../utils/detailManagementAccess";
import DetailAccentCard, {
	DetailSectionHeading,
} from "../../common/detail/DetailAccentCard";
import DetailInstructorBlock from "../../common/detail/DetailInstructorBlock";
import DetailMetaList from "../../common/detail/DetailMetaList";
import CatalogCoverImage from "../../common/catalog/CatalogCoverImage";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import Footer from "../../common/footer/Footer";
import { FooterButton } from "../../common/footer/FooterButton";
import { ContentAccessBadge } from "../../common/badge/FreeBadge";
import Header from "../../common/header/Header";
import Skeleton from "../../common/loading/Skeleton";
import { clipAccessLabel } from "../clipAccess";
import { clipAccessTypeLabel, mergeClipDisplay } from "../clipMeta";
import AdminActionSection from "./action/AdminActionSection";
import RelatedClipsCarousel from "./RelatedClipsCarousel";

const btnOutline =
	"inline-flex min-h-[44px] items-center justify-center rounded-[12px] px-5 font-Vazir-600 text-[14px] no-underline border border-darkBlue/20 bg-white text-darkBlue hover:border-darkBlue/35 transition";

export default function VideoClipDetail() {
	Clarity.init(CLARITY_PROJECT_ID);
	const [isDesktop] = useState(window.innerWidth > 768);
	const { clipId } = useParams();
	const [clip, setClip] = useState(null);
	const [watchPayload, setWatchPayload] = useState(null);
	const [detailError, setDetailError] = useState("");
	const [watchError, setWatchError] = useState("");
	const [isLoadingDetail, setIsLoadingDetail] = useState(true);
	const [isLoadingWatch, setIsLoadingWatch] = useState(true);

	const display = useMemo(
		() => mergeClipDisplay(clip, watchPayload),
		[clip, watchPayload]
	);

	const linkDataList = [
		{ label: "خانه", href: "/" },
		{ label: "کلیپ", href: "/video_clips/" },
		{ label: display?.title || clip?.title || "جزئیات کلیپ" },
	];

	useEffect(() => {
		setIsLoadingDetail(true);
		setDetailError("");
		const token = LoginHelper.getToken();
		api
			.videoClipList({ token })
			.then((list) => {
				const found = (list ?? []).find(
					(item) => String(item.id) === String(clipId)
				);
				if (!found) {
					setDetailError("کلیپ یافت نشد.");
					setClip(null);
				} else {
					setClip(found);
				}
				setIsLoadingDetail(false);
			})
			.catch((err) => {
				setIsLoadingDetail(false);
				setDetailError(
					err?.data?.message || "دریافت اطلاعات کلیپ ناموفق بود."
				);
			});
	}, [clipId]);

	useEffect(() => {
		setIsLoadingWatch(true);
		setWatchError("");
		setWatchPayload(null);
		api
			.videoClipWatch({
				token: LoginHelper.getToken(),
				clip_id: clipId,
			})
			.then((result) => {
				setWatchPayload(result);
				setIsLoadingWatch(false);
			})
			.catch((err) => {
				setIsLoadingWatch(false);
				setWatchError(
					err?.data?.message ||
						"برای تماشای این کلیپ دسترسی لازم را ندارید."
				);
			});
	}, [clipId]);

	const accessType = display?.access_type ?? clip?.access_type;
	const isFreeClip = accessType === "free";
	const access = isFreeClip
		? "رایگان"
		: clipAccessLabel(display ?? clip);
	const title = display?.title || "";
	const description = (display?.description ?? "").trim();

	const showAdminManagement = Boolean(clip && isSiteAdmin());

	const instructors =
		clip?.instructors_detail ??
		display?.instructors_detail ??
		[];

	const expertiseNames = (display?.expertises ?? [])
		.map((item) => item?.name)
		.filter(Boolean)
		.join("، ");

	const metaItems = display
		? [
				{
					label: "نوع دسترسی",
					value: clipAccessTypeLabel(display.access_type),
				},
				expertiseNames
					? { label: "تخصص‌ها", value: expertiseNames }
					: null,
				display.access_type === "credit" && display.credit_cost
					? {
							label: "هزینه اعتبار",
							value: `${display.credit_cost} اعتبار`,
						}
					: null,
			].filter(Boolean)
		: [];

	const clipMain = clip ? (
		<>
			{/* tailwind screens are max-width: row on desktop, stack on md (≤768px) */}
			<div
				dir="ltr"
				className="flex flex-row items-start gap-5 md:flex-col-reverse"
			>
				<DetailAccentCard
					ariaLabel={clip.title}
					className="min-w-0 flex-1 md:w-full md:flex-none"
					bodyClassName="p-5 md:p-6"
				>
					<div dir="rtl" className="flex flex-col gap-4 text-right">
						<h1 className="font-Vazir-700 text-[20px] md:text-[22px] text-darkBlue leading-snug">
							{display?.title}
						</h1>

						<div>
							<DetailSectionHeading className="mb-2 md:text-[15px]">
								اطلاعات کلیپ
							</DetailSectionHeading>
							<div className="flex flex-col gap-3">
								{metaItems.length > 0 && (
									<DetailMetaList items={metaItems} columns={1} />
								)}
								<DetailInstructorBlock instructors={instructors} />
							</div>
						</div>

						{description ? (
							<div>
								<DetailSectionHeading className="mb-2 md:text-[15px]">
									توضیحات
								</DetailSectionHeading>
								<p className="font-Vazir-400 text-[14px] leading-relaxed text-darkBlue/70 whitespace-pre-line">
									{description}
								</p>
							</div>
						) : (
							<p className="font-Vazir-400 text-[14px] text-darkBlue/50">
								توضیحی ثبت نشده است.
							</p>
						)}
					</div>
				</DetailAccentCard>

				<DetailAccentCard
					ariaLabel="تصویر کلیپ"
					className="w-[340px] shrink-0 md:mx-auto md:w-full md:max-w-[380px]"
					bodyClassName="relative p-4 md:p-5"
				>
					{access && (
						<ContentAccessBadge
							label={access}
							unlocked={Boolean(display?.can_watch)}
							className="absolute right-7 top-7 z-10 md:right-8 md:top-8"
						/>
					)}
					<CatalogCoverImage
						src={display?.image_url}
						alt={display?.title || ""}
						roundedClassName="rounded-[12px]"
						aspectClassName="aspect-[16/10]"
						fit="contain"
						className="bg-[#E8F4EC]"
						emptyVariant="play"
					/>
				</DetailAccentCard>
			</div>

			<DetailAccentCard ariaLabel="پخش کلیپ">
				<div>
					<DetailSectionHeading className="mb-4">
						پخش کلیپ
					</DetailSectionHeading>

					{isLoadingWatch && (
						<Skeleton variant="media" className="py-3" />
					)}

					{!isLoadingWatch && watchPayload?.video_url && (
						<div className="overflow-hidden rounded-[12px] bg-black ring-1 ring-darkBlue/15">
							<video
								key={watchPayload.video_url}
								src={watchPayload.video_url}
								controls
								playsInline
								preload="metadata"
								className="w-full max-h-[70vh]"
							/>
						</div>
					)}

					{!isLoadingWatch &&
						watchPayload &&
						!watchPayload.video_url && (
							<p className="py-6 text-center font-Vazir-400 text-[#cc0000]">
								ویدیو برای این کلیپ تنظیم نشده است.
							</p>
						)}

					{!isLoadingWatch && watchError && (
						<div className="rounded-[12px] bg-lightCta/60 px-4 py-5 text-center font-Vazir-400 text-[15px] text-[#cc0000]">
							{watchError}{" "}
							{watchError.includes("وارد") && (
								<Link to="/login/" className="underline text-darkBlue">
									ورود
								</Link>
							)}{" "}
							{watchError.includes("اشتراک") && (
								<Link
									to="/subscriptions/"
									className="underline text-darkBlue"
								>
									مشاهده اشتراک‌ها
								</Link>
							)}
						</div>
					)}
				</div>
			</DetailAccentCard>

			<RelatedClipsCarousel
				clipId={clip.id}
				expertises={clip?.expertises ?? display?.expertises}
				expertiseIds={clip?.expertise_ids ?? display?.expertise_ids}
			/>
		</>
	) : null;

	return (
		<>
			<Helmet>
				<title>{title ? `${title} | کلیپ` : "کلیپ"} | همراهتیم</title>
				<meta
					name="description"
					content={description || "تماشای کلیپ در همراهتیم"}
				/>
			</Helmet>

			<div className="bg-[#F0F6FC] min-h-screen flex flex-col justify-between">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div className="mx-auto w-full max-w-[1200px] px-[10px] md:px-4">
							<Breadcrumb linkDataList={linkDataList} />
						</div>
					</div>

					<div className="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col gap-5 overflow-x-hidden px-[10px] pb-8 pt-6 md:px-4 md:pt-8">
						{isLoadingDetail && (
							<Skeleton variant="detail" type="videoclip" />
						)}

						{!isLoadingDetail && detailError && (
							<DetailAccentCard>
								<p className="text-center font-Vazir-400 text-[#cc0000]">
									{detailError}
								</p>
								<div className="mt-4 flex justify-center">
									<Link to="/video_clips/" className={btnOutline}>
										بازگشت به کلیپ‌ها
									</Link>
								</div>
							</DetailAccentCard>
						)}

						{!isLoadingDetail && clip && (
							isDesktop && showAdminManagement ? (
								<div className="flex flex-row items-start gap-5">
									<div className="flex min-w-0 flex-1 flex-col gap-5">
										{clipMain}
									</div>
									<div className="sticky top-4 w-[300px] shrink-0">
										<AdminActionSection clip={clip} />
									</div>
								</div>
							) : (
								<>
									{clipMain}
									{showAdminManagement && !isDesktop && (
										<AdminActionSection clip={clip} />
									)}
								</>
							)
						)}
					</div>
				</div>
				<div>
					<Footer isDesktop={isDesktop} activeButton={FooterButton.CLIPS} />
				</div>
			</div>
		</>
	);
}
